/**
 * Image Processing Utility
 * Resizes images to 1600x2300px, converts to PNG, and optimizes file size to 300-800KB
 */

import imageCompression from 'browser-image-compression';

export interface ProcessedImage {
  blob: Blob;
  dataUrl: string;
  width: number;
  height: number;
  size: number;
  /** Set when the source was a PDF. */
  pageCount?: number;
}

/**
 * Process an image file: resize to 1600x2300px, convert to PNG, optimize size
 */
export async function processImage(
  file: File,
  targetWidth: number = 1600,
  targetHeight: number = 2300,
  targetSizeMinKB: number = 300,
  targetSizeMaxKB: number = 800
): Promise<ProcessedImage> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const img = new Image();
        img.onload = async () => {
          try {
            // Create canvas
            let canvas = document.createElement('canvas');
            let ctx = canvas.getContext('2d');
            
            if (!ctx) {
              reject(new Error('Could not get canvas context'));
              return;
            }

            // Calculate dimensions maintaining aspect ratio
            let { width, height } = calculateDimensions(
              img.width,
              img.height,
              targetWidth,
              targetHeight
            );

            canvas.width = width;
            canvas.height = height;

            // Draw image on canvas (this resizes it)
            ctx.drawImage(img, 0, 0, width, height);

            // Convert to PNG blob (PNG doesn't support quality parameter)
            // For PNG optimization, we'll adjust dimensions if file size is too large
            let blob = await canvasToBlob(canvas, 1.0, 'image/png');
            let sizeKB = blob.size / 1024;

            // Optimize file size by reducing dimensions if needed
            // PNG doesn't support quality, so we adjust canvas size instead
            if (sizeKB > targetSizeMaxKB) {
              let scaleFactor = 0.9;
              let attempts = 0;
              const maxAttempts = 5;
              
              while (sizeKB > targetSizeMaxKB && attempts < maxAttempts && scaleFactor > 0.5) {
                // Reduce canvas size
                const newWidth = Math.round(width * scaleFactor);
                const newHeight = Math.round(height * scaleFactor);
                
                const tempCanvas = document.createElement('canvas');
                const tempCtx = tempCanvas.getContext('2d');
                if (!tempCtx) break;
                
                tempCanvas.width = newWidth;
                tempCanvas.height = newHeight;
                tempCtx.drawImage(canvas, 0, 0, newWidth, newHeight);
                
                blob = await canvasToBlob(tempCanvas, 1.0, 'image/png');
                sizeKB = blob.size / 1024;
                
                // Update for next iteration
                canvas = tempCanvas;
                width = newWidth;
                height = newHeight;
                
                scaleFactor -= 0.1;
                attempts++;
              }
            }
            
            // If still too small after optimization, that's acceptable
            // PNG files can be smaller than target if the image is simple

            // Convert blob to data URL for preview
            const dataUrl = await blobToDataUrl(blob);

            resolve({
              blob,
              dataUrl,
              width,
              height,
              size: blob.size,
            });
          } catch (error) {
            reject(error);
          }
        };

        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };

        img.src = e.target?.result as string;
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Calculate dimensions maintaining aspect ratio
 */
function calculateDimensions(
  originalWidth: number,
  originalHeight: number,
  targetWidth: number,
  targetHeight: number
): { width: number; height: number } {
  const aspectRatio = originalWidth / originalHeight;
  const targetAspectRatio = targetWidth / targetHeight;

  let width: number;
  let height: number;

  if (aspectRatio > targetAspectRatio) {
    // Image is wider - fit to width
    width = targetWidth;
    height = targetWidth / aspectRatio;
  } else {
    // Image is taller - fit to height
    height = targetHeight;
    width = targetHeight * aspectRatio;
  }

  return { width: Math.round(width), height: Math.round(height) };
}

/**
 * Convert canvas to blob (PNG or JPEG)
 * Note: PNG format ignores quality parameter, JPEG uses it
 */
function canvasToBlob(canvas: HTMLCanvasElement, quality: number, format: 'image/png' | 'image/jpeg' = 'image/png'): Promise<Blob> {
  return new Promise((resolve, reject) => {
    // PNG doesn't support quality, so only pass quality for JPEG
    const options = format === 'image/png' ? undefined : quality;
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to convert canvas to blob'));
        }
      },
      format,
      options
    );
  });
}

/**
 * Convert blob to data URL
 */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Convert canvas to Progressive JPEG blob using browser-image-compression
 * Progressive JPEGs load faster as they display low-quality preview first
 */
async function canvasToProgressiveJPEG(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise(async (resolve, reject) => {
    try {
      // Convert canvas to blob first
      canvas.toBlob(async (blob) => {
        if (!blob) {
          reject(new Error('Failed to convert canvas to blob'));
          return;
        }

        // Convert blob to file
        const file = new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' });

        // Use browser-image-compression for progressive JPEG and optimization
        const options = {
          maxSizeMB: 0.02, // 20KB max
          maxWidthOrHeight: 200,
          useWebWorker: true,
          fileType: 'image/jpeg',
          initialQuality: quality,
        };

        try {
          const compressedFile = await imageCompression(file, options);
          resolve(compressedFile);
        } catch (error) {
          // Fallback to standard JPEG if compression fails
          console.warn('Progressive JPEG compression failed, using standard JPEG:', error);
          resolve(blob);
        }
      }, 'image/jpeg', quality);
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Generate a thumbnail from an image (optimized for quick loading)
 * Creates a 200px wide thumbnail, typically 8-15KB
 * Uses Progressive JPEG for faster perceived loading
 */
export async function generateThumbnail(file: File): Promise<ProcessedImage> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const img = new Image();
        img.onload = async () => {
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            if (!ctx) {
              reject(new Error('Could not get canvas context'));
              return;
            }

            // Optimized thumbnail dimensions: 200px width (for card display)
            // Cards display at 128px height, so 200px width provides good quality
            const thumbnailWidth = 200;
            const thumbnailHeight = (img.height / img.width) * thumbnailWidth;

            canvas.width = thumbnailWidth;
            canvas.height = thumbnailHeight;

            // Draw image on canvas with high-quality scaling
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, thumbnailWidth, thumbnailHeight);

            // Convert to Progressive JPEG with optimized quality for target file size (8-15KB)
            let quality = 0.6; // Start with moderate quality
            let blob = await canvasToProgressiveJPEG(canvas, quality);
            let sizeKB = blob.size / 1024;

            // Optimize for target size (8-15KB, max 20KB)
            if (sizeKB > 15) {
              // Reduce quality if too large
              quality = 0.55;
              while (sizeKB > 15 && quality > 0.35) {
                blob = await canvasToProgressiveJPEG(canvas, quality);
                sizeKB = blob.size / 1024;
                quality -= 0.05;
              }
            } else if (sizeKB < 8 && quality < 0.7) {
              // Increase quality slightly if too small (but don't exceed 15KB)
              quality = Math.min(0.7, quality + 0.05);
              blob = await canvasToProgressiveJPEG(canvas, quality);
              sizeKB = blob.size / 1024;
              // If still under 8KB after increasing quality, that's fine - smaller is better
            }

            const dataUrl = await blobToDataUrl(blob);

            resolve({
              blob,
              dataUrl,
              width: thumbnailWidth,
              height: thumbnailHeight,
              size: blob.size,
            });
          } catch (error) {
            reject(error);
          }
        };

        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };

        img.src = e.target?.result as string;
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Open a PDF with pdf.js. Callers must destroy the returned document when finished.
 */
async function openPdf(file: File): Promise<{ numPages?: number; getPage: (n: number) => Promise<any>; destroy?: () => Promise<void> }> {
  const pdfjsLib = await import('pdfjs-dist');
  pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;
  const data = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({
    data,
    verbosity: 0,
  });
  return loadingTask.promise;
}

const HINT_PAGE_LIMIT = 30;
const HINT_MIN_LETTERS = 40;

/**
 * Hint only. Reads words already in the PDF and does not choose the document type.
 * A photo is not passed here.
 */
export async function inspectPdfUploadHint(
  file: File
): Promise<{ looksLikeConsolidated: boolean; pageCount: number; text: string }> {
  const pdf = await openPdf(file);
  try {
    const pageCount = pdf.numPages || 0;
    const last = Math.min(pageCount, HINT_PAGE_LIMIT);
    let text = '';
    for (let pageNumber = 1; pageNumber <= last; pageNumber++) {
      const page = await pdf.getPage(pageNumber);
      const content = await page.getTextContent();
      const items = (content?.items || []) as Array<{ str?: string }>;
      for (const item of items) {
        if (item?.str) text += `${item.str}\n`;
      }
    }
    const letters = text.match(/[A-Za-z0-9]/g);
    const readable = (letters?.length ?? 0) >= HINT_MIN_LETTERS;
    return {
      looksLikeConsolidated: readable && pageCount > 3 && /consolidated/i.test(text),
      pageCount,
      text,
    };
  } finally {
    try {
      await pdf.destroy?.();
    } catch {
      /* ignore */
    }
  }
}

/** Number of pages in a PDF. Images are not passed here. */
export async function countPdfPages(file: File): Promise<number> {
  const pdf = await openPdf(file);
  try {
    const n = pdf.numPages;
    if (!Number.isInteger(n) || !n || n < 1) {
      throw new Error('PDF did not report a page count');
    }
    return n;
  } finally {
    try {
      await pdf.destroy?.();
    } catch {
      /* ignore */
    }
  }
}

/**
 * Generate thumbnail from PDF first page
 * Uses pdfjs-dist to render first page to canvas, then generates thumbnail
 */
export async function generatePDFThumbnail(file: File): Promise<ProcessedImage> {
  return new Promise(async (resolve, reject) => {
    let pdf: { numPages?: number; getPage: (n: number) => Promise<any>; destroy?: () => Promise<void> } | undefined;
    try {
      pdf = await openPdf(file);
      const pageCount = Math.max(1, Number(pdf.numPages) || 1);

      // Get first page
      const page = await pdf.getPage(1);
      
      // Calculate viewport (scale 2.0 for good quality)
      const viewport = page.getViewport({ scale: 2.0 });
      
      // Create canvas
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const context = canvas.getContext('2d');
      
      if (!context) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      
      // Render PDF page to canvas
      await page.render({
        canvasContext: context,
        viewport: viewport,
        // @ts-ignore - pdfjs-dist types may be incomplete, but this works at runtime
      } as any).promise;
      
      // Now use existing thumbnail generation logic (resize to 200px, optimize)
      const thumbnailWidth = 200;
      const thumbnailHeight = (canvas.height / canvas.width) * thumbnailWidth;
      
      // Create thumbnail canvas
      const thumbCanvas = document.createElement('canvas');
      thumbCanvas.width = thumbnailWidth;
      thumbCanvas.height = thumbnailHeight;
      const thumbContext = thumbCanvas.getContext('2d');
      
      if (!thumbContext) {
        reject(new Error('Could not get thumbnail canvas context'));
        return;
      }
      
      // Draw resized image
      thumbContext.imageSmoothingEnabled = true;
      thumbContext.imageSmoothingQuality = 'high';
      thumbContext.drawImage(canvas, 0, 0, thumbnailWidth, thumbnailHeight);
      
      // Convert to Progressive JPEG with optimized quality (8-15KB target)
      let quality = 0.6;
      let blob = await canvasToProgressiveJPEG(thumbCanvas, quality);
      let sizeKB = blob.size / 1024;
      
      // Optimize for target size (8-15KB, max 20KB)
      if (sizeKB > 15) {
        quality = 0.55;
        while (sizeKB > 15 && quality > 0.35) {
          blob = await canvasToProgressiveJPEG(thumbCanvas, quality);
          sizeKB = blob.size / 1024;
          quality -= 0.05;
        }
      } else if (sizeKB < 8 && quality < 0.7) {
        quality = Math.min(0.7, quality + 0.05);
        blob = await canvasToProgressiveJPEG(thumbCanvas, quality);
        sizeKB = blob.size / 1024;
      }
      
      const dataUrl = await blobToDataUrl(blob);

      resolve({
        blob,
        dataUrl,
        width: thumbnailWidth,
        height: thumbnailHeight,
        size: blob.size,
        pageCount,
      });
    } catch (error) {
      reject(error);
    } finally {
      try {
        await pdf?.destroy?.();
      } catch {
        /* ignore */
      }
    }
  });
}

/**
 * Process PDF file (for PDFs, we'll keep as-is or convert first page to image)
 * Note: PDF processing requires PDF.js library for full support
 */
export async function processPDF(file: File): Promise<ProcessedImage> {
  // For now, return PDF as-is
  // In production, you might want to convert first page to PNG using PDF.js
  const blob = file;
  const dataUrl = await blobToDataUrl(blob);
  
  return {
    blob,
    dataUrl,
    width: 1600,
    height: 2300,
    size: blob.size,
  };
}

/**
 * Check if file is an image
 */
export function isImageFile(file: File): boolean {
  if (file.type.startsWith('image/')) {
    return true;
  }
  const n = file.name.toLowerCase();
  return n.endsWith('.heic') || n.endsWith('.heif');
}

/**
 * Check if file is a PDF
 */
export function isPDFFile(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

